const KINDS = ["audio", "video"];

const ENCODING_LIMITS = {
    audio: { maxBitrate: 32000 },
    video: { maxBitrate: 150000, maxFramerate: 15 },
};

export default class PeerLink {
    constructor({
        remoteId,
        polite,
        initiator,
        iceServers,
        sendSignal,
        onTrack,
        onStateChange,
    }) {
        this.remoteId = remoteId;
        this.polite = polite;
        this.sendSignal = sendSignal;
        this.onTrack = onTrack;
        this.onStateChange = onStateChange;

        this.localTracks = { audio: null, video: null };
        this.transceivers = { audio: null, video: null };
        this.makingOffer = false;
        this.ignoreOffer = false;
        this.isSettingRemoteAnswerPending = false;
        this.signalQueue = Promise.resolve();
        this.closed = false;

        this.pc = new RTCPeerConnection({ iceServers });
        this.pc.onnegotiationneeded = () => this._negotiate();
        this.pc.onicecandidate = ({ candidate }) => {
            if (candidate) this.sendSignal({ candidate: candidate.toJSON() });
        };
        this.pc.onicecandidateerror = ({ url, errorCode, errorText }) => {
            if (url)
                console.warn(
                    `[Voice] ICE server ${url} failed: ${errorCode} ${errorText}`,
                );
        };
        this.pc.ontrack = ({ track }) => this.onTrack(track);
        this.pc.onconnectionstatechange = () => this._onConnectionStateChange();

        if (initiator) {
            KINDS.forEach((kind) => {
                this.transceivers[kind] = this.pc.addTransceiver(kind, {
                    direction: "sendrecv",
                });
            });
        }
    }

    async setLocalTrack(kind, track) {
        this.localTracks[kind] = track;
        const transceiver = this.transceivers[kind];
        if (!transceiver || this.closed) return;

        try {
            await transceiver.sender.replaceTrack(track);
        } catch (e) {
            console.error(
                `[Voice] Could not replace ${kind} track for`,
                this.remoteId,
                e,
            );
        }
    }

    handleSignal(signal) {
        this.signalQueue = this.signalQueue
            .then(() => this._processSignal(signal))
            .catch((e) =>
                console.error("[Voice] Signaling error with", this.remoteId, e),
            );
        return this.signalQueue;
    }

    close() {
        this.closed = true;
        this.pc.onnegotiationneeded = null;
        this.pc.onicecandidate = null;
        this.pc.onicecandidateerror = null;
        this.pc.ontrack = null;
        this.pc.onconnectionstatechange = null;
        this.pc.close();
    }

    async _processSignal({ description, candidate }) {
        if (this.closed) return;

        if (description) {
            const readyForOffer =
                !this.makingOffer &&
                (this.pc.signalingState === "stable" ||
                    this.isSettingRemoteAnswerPending);
            const offerCollision =
                description.type === "offer" && !readyForOffer;

            this.ignoreOffer = !this.polite && offerCollision;
            if (this.ignoreOffer) return;

            this.isSettingRemoteAnswerPending = description.type === "answer";
            await this.pc.setRemoteDescription(description);
            this.isSettingRemoteAnswerPending = false;

            if (description.type === "offer") {
                await this._adoptTransceivers();
                await this.pc.setLocalDescription();
                this._sendLocalDescription();
            }
        } else if (candidate) {
            try {
                await this.pc.addIceCandidate(candidate);
            } catch (e) {
                if (!this.ignoreOffer) throw e;
            }
        }
    }

    async _negotiate() {
        try {
            this.makingOffer = true;
            await this.pc.setLocalDescription();
            this._sendLocalDescription();
        } catch (e) {
            console.error("[Voice] Negotiation failed with", this.remoteId, e);
        } finally {
            this.makingOffer = false;
        }
    }

    _sendLocalDescription() {
        const { type, sdp } = this.pc.localDescription;
        this.sendSignal({ description: { type, sdp } });
    }

    async _adoptTransceivers() {
        const available = this.pc.getTransceivers();

        for (const kind of KINDS) {
            if (this.transceivers[kind]) continue;

            const transceiver = available.find(
                (t) => t.receiver.track.kind === kind,
            );
            if (!transceiver) continue;

            this.transceivers[kind] = transceiver;
            transceiver.direction = "sendrecv";
            await transceiver.sender.replaceTrack(this.localTracks[kind]);
        }
    }

    _onConnectionStateChange() {
        const state = this.pc.connectionState;

        if (state === "connected") this._limitBitrate();
        if (state === "failed") {
            console.warn(
                "[Voice] No network path to",
                this.remoteId,
                "- retrying ICE (needs a TURN server across restrictive NATs)",
            );
            this.pc.restartIce();
        }

        this.onStateChange(state);
    }

    async _limitBitrate() {
        for (const kind of KINDS) {
            const sender = this.transceivers[kind]?.sender;
            if (!sender) continue;

            try {
                const params = sender.getParameters();
                if (!params.encodings || params.encodings.length === 0)
                    continue;

                Object.assign(params.encodings[0], ENCODING_LIMITS[kind]);
                await sender.setParameters(params);
            } catch (e) {
                console.warn(
                    `[Voice] Could not limit ${kind} bitrate for`,
                    this.remoteId,
                    e,
                );
            }
        }
    }
}
